import React from 'react';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper';

import { convertThumbnailToPictureProps } from '@tager/web-modules';

import { ImagesGallery } from '@/typings/model';
import Picture from '@/components/Picture';
import { media } from '@/utils/mixin';
import { colors } from '@/constants/theme';
import NavArrow from '@/assets/svg/nav-arrow.svg';
import { NavButtonWrapper, NavigationButton } from '@/components/styles';

SwiperCore.use([Navigation, Pagination, Autoplay]);

interface Props {
  gallery: ImagesGallery[];
}

function GallerySlider({ gallery }: Props) {
  const pagination = {
    clickable: true,
    renderBullet: (index: number, className: string) =>
      `<div class="${className}"><span>${index + 1}</span></div>`,
  };

  return (
    <Component>
      <Swiper
        slidesPerView={1}
        speed={600}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop
        navigation={{
          prevEl: `.swiper-prev`,
          nextEl: `.swiper-next`,
        }}
        pagination={pagination}
      >
        {gallery &&
          gallery.map(({ image }, index) => (
            <SwiperSlide key={index}>
              <SliderImage
                loading="lazy"
                {...convertThumbnailToPictureProps(image)}
              />
            </SwiperSlide>
          ))}
      </Swiper>

      <StyledNavButtonWrapper className="swiper-prev" prev>
        <StyledNavigationButton prev>
          <NavArrow />
        </StyledNavigationButton>
      </StyledNavButtonWrapper>
      <StyledNavButtonWrapper className="swiper-next" next>
        <StyledNavigationButton next>
          <NavArrow />
        </StyledNavigationButton>
      </StyledNavButtonWrapper>
    </Component>
  );
}

export default GallerySlider;

const Component = styled.div`
  position: relative;

  .swiper {
    &-pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      bottom: 50px;

      ${media.mobile(css`
        bottom: 30px;
      `)}

      &-bullet {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: ${colors.white};
        content: none;

        span {
          visibility: hidden;
        }
      }
    }
  }
`;

const SliderImage = styled(Picture)`
  position: relative;
  padding-top: 66.8%;

  picture,
  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    margin: 0;
  }

  img {
    object-fit: cover;
  }
`;

const StyledNavButtonWrapper = styled(NavButtonWrapper)<{
  next?: boolean;
  prev?: boolean;
}>`
  ${(props) =>
    props.next &&
    css`
      right: 0;
      padding-left: 11%;

      ${media.tablet(css`
        padding-left: 5.5%;
      `)}

      ${media.mobile(css`
        padding-left: 10%;
      `)}
    `}
  ${(props) =>
    props.prev &&
    css`
      padding-right: 11%;

      ${media.tablet(css`
        padding-right: 5.5%;
      `)}

      ${media.mobile(css`
        padding-right: 10%;
      `)}
    `}

  ${media.mobile(css`
    display: block;
  `)}
`;

const StyledNavigationButton = styled(NavigationButton)``;
